// ─── Auth Zustand store ────────────────────────────────────────────────────────

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, LoginCredentials } from '../types';
import { authService, LoginOutcome } from '../api/authService';
import logger from '../utils/logger';

interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  completeTwoFactor: (challengeId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const outcome = await authService.login(credentials);
          if (outcome.kind === 'authenticated') {
            sessionStorage.setItem('auth_token', outcome.token);
            sessionStorage.setItem('refresh_token', outcome.refreshToken);
            set({
              user: outcome.user,
              token: outcome.token,
              isAuthenticated: true,
              isLoading: false,
            });
            logger.info('AuthStore: login success', { userId: outcome.user.id });
          } else {
            // 2FA challenge — do NOT mark authenticated; caller routes to /auth/verify-2fa.
            set({ isLoading: false });
            logger.info('AuthStore: 2FA challenge issued', { method: outcome.method });
          }
          return outcome;
        } catch (err: unknown) {
          const message = (err as { message?: string })?.message ?? 'Login failed';
          set({ error: message, isLoading: false });
          logger.error('AuthStore: login failed', err);
          throw err;
        }
      },

      completeTwoFactor: async (challengeId, code) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.verifyTwoFactor(challengeId, code);
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
          });
          logger.info('AuthStore: 2FA verified', { userId: response.user.id });
        } catch (err: unknown) {
          const message = (err as { message?: string })?.message ?? 'Verification failed';
          set({ error: message, isLoading: false });
          logger.error('AuthStore: 2FA verify failed', err);
          throw err;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
        } finally {
          set({ user: null, token: null, isAuthenticated: false, isLoading: false, error: null });
          logger.info('AuthStore: logout');
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user) => set({ user }),

      refreshUser: async () => {
        try {
          const user = await authService.getCurrentUser();
          set({ user });
        } catch (err) {
          logger.warn('AuthStore: refreshUser failed', err);
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
    }
  )
);
